// src/pages/contact/controllers/Contact.controller.tsx
import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { ContactViewProps, ContactFormData, FormErrors } from "../types";
import { useContacts } from "../../../hooks/useContacts";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";

export function ContactController() {
  useDocumentTitle(
    "Contato",
    "Entre em contato com Cassio Silva Takarada por e-mail ou redes sociais."
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean | null>(null);

  const { createContact } = useContacts();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  const socialMedia = [
    {
      id: 1,
      name: "LinkedIn",
      url: "https://www.linkedin.com/in/cassio-takarada/",
      icon: "ph-linkedin-logo",
    },
    {
      id: 2,
      name: "GitHub",
      url: "https://github.com/cassiotakarada",
      icon: "ph-github-logo",
    },
    {
      id: 3,
      name: "Twitter",
      url: "https://twitter.com/cassiodeveloper",
      icon: "ph-twitter-logo",
    },
    {
      id: 4,
      name: "Instagram",
      url: "https://instagram.com/cassio.dev",
      icon: "ph-instagram-logo",
    },
  ];

  const contactInfo = {
    email: "cassio_takarada@hotmail.com",
    location: "Franca - SP, Brazil",
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = "Nome é obrigatório";
    } else if (formData.name.trim().length < 1) {
      errors.name = "Nome inválido";
    }

    if (!formData.email.trim()) {
      errors.email = "E-mail é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "E-mail inválido";
    }

    if (!formData.subject.trim()) {
      errors.subject = "Assunto é obrigatório";
    } else if (formData.subject.trim().length < 2) {
      errors.subject = "Assunto muito curto";
    }

    if (!formData.message.trim()) {
      errors.message = "Mensagem é obrigatória";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Mensagem muito curta (mínimo 10 caracteres)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitSuccess(null);

    const created = await createContact({
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
    });

    setIsSubmitting(false);

    if (created) {
      setSubmitSuccess(true);
      setTimeout(() => {
        resetForm();
      }, 3000);
    } else {
      setSubmitSuccess(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: "",
    });
    setFormErrors({});
    setSubmitSuccess(null);
  };

  const viewProps: ContactViewProps = {
    isLoading,
    formData,
    formErrors,
    isSubmitting,
    submitSuccess,
    socialMedia,
    contactInfo,
    handleInputChange,
    handleSubmit,
    resetForm,
  };

  return viewProps;
}

export default ContactController;
